// 009 FR-040 (local/staging): the test gateway's sheet. While mounted it answers the fake adapter:
// Pay successfully, Fail payment or Cancel. Production checkouts never use it (the server refuses
// the fake provider there).
import { useEffect, useRef, useState } from "react";
import { Modal, StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";
import { Text } from "@/ui/Text";

import { CHECKOUT_COPY } from "./copy";
import { type GatewayResult, registerFakeSheet } from "./payment";
import { formatPaise } from "@/shop/format";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: t.color.scrim,
      justifyContent: "flex-end",
      alignItems: "center",
    },
    sheet: {
      width: "100%",
      maxWidth: 480,
      padding: t.space.x2l,
      gap: t.space.md,
      borderTopLeftRadius: t.radius.card,
      borderTopRightRadius: t.radius.card,
      backgroundColor: t.color.background,
    },
  }),
);

export function FakePaymentSheet() {
  const styles = useStyles();
  const [amount, setAmount] = useState<number | null>(null);
  const answer = useRef<((r: GatewayResult) => void) | null>(null);
  useEffect(() => {
    registerFakeSheet(
      (session) =>
        new Promise<GatewayResult>((resolve) => {
          answer.current = resolve;
          setAmount(session.amountInPaise);
        }),
    );
    return () => registerFakeSheet(null);
  }, []);
  const done = (r: GatewayResult) => {
    setAmount(null);
    answer.current?.(r);
    answer.current = null;
  };
  return (
    <Modal
      transparent
      visible={amount !== null}
      animationType="slide"
      onRequestClose={() => done({ type: "cancelled" })}
    >
      <View style={styles.backdrop}>
        <View
          style={styles.sheet}
          role="dialog"
          aria-label={CHECKOUT_COPY.fakeTitle}
          testID="fake-payment-sheet"
        >
          <Text variant="heading" accessibilityRole="header">
            {CHECKOUT_COPY.fakeTitle}
          </Text>
          <Text tone="muted">{CHECKOUT_COPY.fakeLead}</Text>
          {amount !== null ? (
            <Text weight="bold" size="x2l">
              {formatPaise(amount)}
            </Text>
          ) : null}
          <Button
            onPress={() => done({ type: "success", result: null })}
            testID="fake-pay-success"
          >
            {CHECKOUT_COPY.fakeSuccess}
          </Button>
          <Button
            variant="outline"
            onPress={() => done({ type: "failure" })}
            testID="fake-pay-fail"
          >
            {CHECKOUT_COPY.fakeFail}
          </Button>
          <Button
            variant="outline"
            onPress={() => done({ type: "cancelled" })}
            testID="fake-pay-cancel"
          >
            {CHECKOUT_COPY.fakeCancel}
          </Button>
        </View>
      </View>
    </Modal>
  );
}
